import { numberToPixel } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Styling/conversions";
import { ComponentLoggingEventParams } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/types";
import { MIN_WIDTH_BREAKPOINTS } from "@faire/web--source/common/media";
import { ContentWidth } from "@faire/web-api--source/indigofair/data/ContentWidth";
import { DisplayPageType } from "@faire/web-api--source/indigofair/data/DisplayPageType";
import { ILayoutElement } from "@faire/web-api--source/indigofair/data/ILayoutElement";

import { MAX_CONTENT_WIDTH } from "@faire/retailer/features/Marketplace/styleConsts";

/**
 * @deprecated Use Section level content width instead. We do not send component content width from BE, so most of this is not used.
 * This functionality is deprecated in favour of setting content width at the Section level.
 * Components should avoid setting their own content widths to prevent conflicts with
 * Section level content width and Layout determined component widths.
 */
export const getContentWidthCss = (
  width: keyof typeof ContentWidth | undefined,
  loggingData: ComponentLoggingEventParams
): React.CSSProperties => {
  return {
    maxWidth: getContentWidth(
      getServerContentWidthToClientSideContentWidth(width) ??
        getContentWidthFallback(loggingData)
    ),
  };
};

// TODO: https://fairewholesale.atlassian.net/browse/FD-208072
// All of the conversion from server side to client side content width should be done in the server side
enum ClientSideContentWidth {
  HOMEPAGE_STANDARD,
  CATEGORY_PAGE_STANDARD,
  XLARGE,
  XXLARGE,
}

const contentWidthMap: Record<ClientSideContentWidth, number> = {
  [ClientSideContentWidth.HOMEPAGE_STANDARD]: 1280,
  [ClientSideContentWidth.CATEGORY_PAGE_STANDARD]: MAX_CONTENT_WIDTH,
  [ClientSideContentWidth.XLARGE]: MIN_WIDTH_BREAKPOINTS.XLARGE,
  [ClientSideContentWidth.XXLARGE]: MIN_WIDTH_BREAKPOINTS.XXLARGE,
};

const getContentWidth = (width: ClientSideContentWidth): string => {
  return numberToPixel(contentWidthMap[width]) ?? "auto";
};

const getServerContentWidthToClientSideContentWidth = (
  width: keyof typeof ContentWidth | undefined
): ClientSideContentWidth | undefined => {
  switch (width) {
    case ContentWidth.DESKTOP_STANDARD:
      return ClientSideContentWidth.HOMEPAGE_STANDARD;
    default:
      return undefined;
  }
};

// TODO: https://fairewholesale.atlassian.net/browse/FD-208072
// Fallback logic should be removed once it is prevalent on the server
const getContentWidthFallback = (
  loggingData: ComponentLoggingEventParams
): ClientSideContentWidth => {
  if (loggingData.view_display_page === DisplayPageType.HOMEPAGE) {
    return ClientSideContentWidth.HOMEPAGE_STANDARD;
  }

  if (loggingData.view_display_page === DisplayPageType.SEARCH_PAGE) {
    return ClientSideContentWidth.XXLARGE;
  }

  if (loggingData.view_display_page === DisplayPageType.CATEGORY_PAGE) {
    if (
      loggingData.component_type ===
      ILayoutElement.Type.MULTI_IMAGE_TITLE_BANNER
    ) {
      return ClientSideContentWidth.XXLARGE;
    }

    if (loggingData.component_type === ILayoutElement.Type.HORIZONTAL_LINE) {
      return ClientSideContentWidth.XLARGE;
    }

    // temporary solution for full width category, should migrate to BE driven after
    // https://www.notion.so/faire/Backend-driven-SDUI-spacing-10d2efb5c25a8034a76ef050f524d1b8 is implemented
    if (
      loggingData.component_name?.startsWith("browse_") &&
      loggingData.view_instance_identifier !== "gifts"
    ) {
      return ClientSideContentWidth.CATEGORY_PAGE_STANDARD;
    }

    return ClientSideContentWidth.XXLARGE;
  }

  if (loggingData.view_display_page === DisplayPageType.BRAND_DETAILS_PAGE) {
    return ClientSideContentWidth.XXLARGE;
  }

  return ClientSideContentWidth.HOMEPAGE_STANDARD;
};
